// frontend/src/components/FunkyPigeonKpiDashboard.js
import axios from 'axios';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Tooltip,
  CircularProgress,
  useTheme,
  styled,
  TextField,
  Button,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import GradientIcon from '@mui/icons-material/Gradient';
import CollectionsIcon from '@mui/icons-material/Collections';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { getDashboardSummary, getKpiHistory} from '../services/api';
import { format } from 'date-fns';
import Layout from './Layout';
import HourlyStatisticsTable from './HourlyStatisticsTable';

// helper to compute progress percent
const percent = (done, total) => (total > 0 ? Math.round((done / total) * 100) : 0);

// cutoff labels
const cutoffInfo = {
  'Giant Cards': 'Evri Next Day, cutoff 21:00 (Mon–Fri)',
  'Wrapping Paper': 'Evri Next Day, cutoff 13:00 (Mon–Fri)',
  'GiftOnly': 'Royal Mail Tracked, cutoff 18:30 (Mon–Fri); 14:00 (Sat); Sun n/a',
  'Card+Gift': 'Royal Mail Tracked, cutoff 18:30 (Mon–Fri); 14:00 (Sat); Sun n/a',
};

// icon map
const CategoryIcon = {
  'Giant Cards': <CollectionsIcon fontSize="large" />,
  'Wrapping Paper': <GradientIcon fontSize="large" />,
  'GiftOnly': <CardGiftcardIcon fontSize="large" />,
  'Card+Gift': <CollectionsIcon fontSize="large" />,
};

// colored, hover-lift card
const CategoryCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'bordercolor',
})(({ theme, bordercolor }) => ({
  borderLeft: `6px solid ${bordercolor}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  transition: 'transform 0.2s, box-shadow 0.2s',
  width: '100%',
  minHeight: 170,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[6],
  },
  overflow: 'hidden',
}));

// metric block
const Metric = ({ label, value, color }) => (
  <Box textAlign="center" sx={{ flex: 1 }}>
    <Typography variant="subtitle2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h3" sx={{ color, fontWeight: 600, mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

// simple TabPanel
function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

export default function FunkyPigeonKpiDashboard() {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState({});
  const [tab, setTab] = useState(0);
  const categories = ['Giant Cards', 'Wrapping Paper', 'GiftOnly', 'Card+Gift'];

  // history (using Dayjs)
  const [start, setStart] = useState(dayjs().subtract(6, 'day'));
  const [end, setEnd] = useState(dayjs());
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  // fetch summary every 15s
  useEffect(() => {
    let mounted = true;
    const fetchSummary = () => {
      getDashboardSummary()
        .then((summary) => {
          if (!mounted) return;
          const fp = summary.clients.find((c) => c.clientName === 'Funky Pigeon') || {};
          setClient(fp);
          setLoading(false);
        })
        .catch(() => mounted && setLoading(false));
    };
    fetchSummary();
    const id = setInterval(fetchSummary, 15000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // fetch history when date range changes
  useEffect(() => {
    setHistLoading(true);
    getKpiHistory(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'))
      .then((resp) => {
        if (resp.success) setHistory(resp.data);
      })
      .catch(console.error)
      .finally(() => setHistLoading(false));
  }, [start, end]);

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress size={48} />
        </Box>
      </Layout>
    );
  }

  const histCols = [
    { field: 'snapshotDate', headerName: 'Date', width: 120 },
    { field: 'giant_required', headerName: 'XL To-do', width: 120 },
    { field: 'giant_dispatched', headerName: 'XL Done', width: 120 },
    { field: 'giant_total', headerName: 'XL Total', width: 120 },
    { field: 'giant_pct', headerName: 'XL %', width: 100 },
    { field: 'wrapping_required', headerName: 'WP To-do', width: 120 },
    { field: 'wrapping_dispatched', headerName: 'WP Done', width: 120 },
    { field: 'wrapping_total', headerName: 'WP Total', width: 120 },
    { field: 'wrapping_pct', headerName: 'WP %', width: 100 },
    { field: 'giftonly_required', headerName: 'Gifts To-do', width: 130 },
    { field: 'giftonly_dispatched', headerName: 'Gifts Done', width: 130 },
    { field: 'giftonly_total', headerName: 'Gifts Total', width: 130 },
    { field: 'giftonly_pct', headerName: 'Gifts %', width: 100 },
    { field: 'cardgift_required', headerName: 'CG To-do', width: 120 },
    { field: 'cardgift_dispatched', headerName: 'CG Done', width: 120 },
    { field: 'cardgift_total', headerName: 'CG Total', width: 120 },
    { field: 'cardgift_pct', headerName: 'CG %', width: 100 },
  ];

  const exportCSV = async () => {
    try {
      const s = start.format('YYYY-MM-DD');
      const e = end.format('YYYY-MM-DD');
      const resp = await axios.get(`/api/kpi-history/csv?start=${s}&end=${e}`, {
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'funky-pigeon-kpi-history.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed', err);
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Funky Pigeon — KPI Dashboard
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label="Today" />
          <Tab label="Tomorrow" />
          <Tab label="Hourly Stats" />
        </Tabs>

        {/* ─── TODAY CARDS ───────────────────────── */}
        <TabPanel value={tab} index={0}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {categories.map((cat) => {
              const req = client.requiredDispatchByCategory?.[cat] || 0;
              const done = client.dispatchedByCategory?.[cat] || 0;
              const tot = req + done;
              const pct = percent(done, tot);
              let bc = theme.palette.primary.main;
              if (cat === 'Giant Cards') bc = theme.palette.secondary.main;
              if (cat === 'GiftOnly') bc = theme.palette.success.main;
              if (cat === 'Card+Gift') bc = theme.palette.warning.main;

              return (
                <Box key={cat} sx={{ minWidth: 0, display: 'flex' }}>
                  <CategoryCard bordercolor={bc}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        background: theme.palette.grey[100],
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Box sx={{ mr: 1, color: bc }}>{CategoryIcon[cat]}</Box>
                      <Typography variant="h6">{cat}</Typography>
                      <Tooltip title={cutoffInfo[cat]}>
                        <Box
                          component="span"
                          sx={{
                            ml: 'auto',
                            fontSize: '1rem',
                            bgcolor: 'rgba(0,0,0,0.05)',
                            px: 1,
                            borderRadius: 1,
                            cursor: 'help',
                          }}
                        >
                          ⏰
                        </Box>
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        px: 3,
                        py: 2.5,
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Metric
                        label="To do"
                        value={req}
                        color={theme.palette.error.main}
                      />
                      <Metric
                        label="Done"
                        value={done}
                        color={theme.palette.success.main}
                      />
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                        >
                          Progress
                        </Typography>
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            mt: 0.5,
                          }}
                        >
                          <CircularProgress
                            variant="determinate"
                            value={pct}
                            size={64}
                            thickness={5}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h6">{pct}%</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Metric
                        label="Total"
                        value={tot}
                        color={theme.palette.primary.main}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 3,
                        pb: 2,
                        mt: 'auto',
                      }}
                    >
                      {cat === 'Giant Cards' ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Include Lindt and Nutella.
                        </Typography>
                      ) : (
                        <Box />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        As at {format(new Date(), 'dd MMM yyyy HH:mm')}
                      </Typography>
                    </Box>
                  </CategoryCard>
                </Box>
              );
            })}
          </Box>
        </TabPanel>

        {/* ─── TOMORROW CARDS ───────────────────────── */}
        <TabPanel value={tab} index={1}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 3,
              alignItems: 'stretch',
            }}
          >
            {categories.map((cat) => {
              const req =
                client.requiredDispatchByCategoryTomorrow?.[cat] || 0;
              const done = 0;
              const tot = req;
              const pct = percent(done, tot);
              let bc = theme.palette.primary.main;
              if (cat === 'Giant Cards') bc = theme.palette.secondary.main;
              if (cat === 'GiftOnly') bc = theme.palette.success.main;
              if (cat === 'Card+Gift') bc = theme.palette.warning.main;

              return (
                <Box key={cat} sx={{ minWidth: 0, display: 'flex' }}>
                  <CategoryCard bordercolor={bc}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        background: theme.palette.grey[100],
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Box sx={{ mr: 1, color: bc }}>{CategoryIcon[cat]}</Box>
                      <Typography variant="h6">{cat}</Typography>
                      <Tooltip title={cutoffInfo[cat]}>
                        <Box
                          component="span"
                          sx={{
                            ml: 'auto',
                            fontSize: '1rem',
                            bgcolor: 'rgba(0,0,0,0.05)',
                            px: 1,
                            borderRadius: 1,
                            cursor: 'help',
                          }}
                        >
                          ⏰
                        </Box>
                      </Tooltip>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        px: 3,
                        py: 2.5,
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Metric
                        label="To do"
                        value={req}
                        color={theme.palette.error.main}
                      />
                      <Metric
                        label="Done"
                        value={done}
                        color={theme.palette.success.main}
                      />
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                        >
                          Progress
                        </Typography>
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            mt: 0.5,
                          }}
                        >
                          <CircularProgress
                            variant="determinate"
                            value={pct}
                            size={64}
                            thickness={5}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography variant="h6">{pct}%</Typography>
                          </Box>
                        </Box>
                      </Box>
                      <Metric
                        label="Total"
                        value={tot}
                        color={theme.palette.primary.main}
                      />
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 3,
                        pb: 2,
                        mt: 'auto',
                      }}
                    >
                      {cat === 'Giant Cards' ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Include Lindt and Nutella.
                        </Typography>
                      ) : (
                        <Box />
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        As at {format(new Date(), 'dd MMM yyyy HH:mm')}
                      </Typography>
                    </Box>
                  </CategoryCard>
                </Box>
              );
            })}
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={2}>
  <HourlyStatisticsTable />
</TabPanel>

        {/* ─── KPI HISTORY ───────────────────────────── */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            KPI History
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <DatePicker
              label="From"
              value={start}
              onChange={setStart}
              inputFormat="DD/MM/YYYY"
              renderInput={(props) => <TextField {...props} size="small" />}
            />
            <DatePicker
              label="To"
              value={end}
              onChange={setEnd}
              inputFormat="DD/MM/YYYY"
              renderInput={(props) => <TextField {...props} size="small" />}
            />
            <Button variant="outlined" size="small" onClick={exportCSV}>
              Export CSV
            </Button>
          </Box>
          <Paper sx={{ width: '100%', height: 400, overflowX: 'auto' }}>
            {histLoading ? (
              <Box
                sx={{
                  height: 400,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%', height: '100%', overflowX: 'auto' }}>
                <Box sx={{ width: 'max-content', height: '100%' }}>
                  <DataGrid
                    rows={history}
                    columns={histCols}
                    getRowId={(r) => r.snapshotDate}
                    pageSize={14}
                    rowsPerPageOptions={[14]}
                    disableSelectionOnClick
                  />
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    </Layout>
  );
}
